import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Calendar, MapPin, Users, Music, 
  DollarSign, Clock, Building, MessageSquare, Trash2, Eye,
  CheckCircle, XCircle, AlertCircle, RefreshCw, Shield, ShieldAlert
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  quoted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  booked: 'bg-green-500/20 text-green-400 border-green-500/30',
  declined: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const statusIcons = {
  new: AlertCircle,
  contacted: Phone,
  quoted: DollarSign,
  booked: CheckCircle,
  declined: XCircle
};

// reCAPTCHA v3 score → visual signal. Higher score = more human-like.
const RecaptchaBadge = ({ score }) => {
  if (score === null || score === undefined) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-stone-500/15 text-stone-400 border-stone-500/30"
        title="Submitted before spam protection was enabled"
        data-testid="recaptcha-badge-none"
      >
        <Shield className="w-2.5 h-2.5" />
        no-score
      </span>
    );
  }
  const s = Number(score);
  const isBot = s < 0.5;
  const isBorderline = s >= 0.5 && s < 0.7;
  const cls = isBot
    ? 'bg-red-500/15 text-red-400 border-red-500/30'
    : isBorderline
      ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
      : 'bg-green-500/15 text-green-400 border-green-500/30';
  const Icon = isBot ? ShieldAlert : Shield;
  const label = isBot ? 'likely bot' : isBorderline ? 'borderline' : 'human';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}
      title={`reCAPTCHA v3 score: ${s.toFixed(2)} — ${label}`}
      data-testid="recaptcha-badge"
    >
      <Icon className="w-2.5 h-2.5" />
      {s.toFixed(2)} · {label}
    </span>
  );
};

export const AdminQuoteRequests = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await adminFetch('/admin/quote-requests');
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const response = await adminFetch(`/admin/quote-requests/${quoteId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote request?')) return;
    
    try {
      const response = await adminFetch(`/admin/quote-requests/${quoteId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setQuotes(quotes.filter(q => q.id !== quoteId));
        setSelectedQuote(null);
        toast.success('Quote request deleted');
      }
    } catch (error) {
      toast.error('Failed to delete quote request');
    }
  };

  const filteredQuotes = (
    filterStatus === 'all'
      ? quotes
      : filterStatus === 'spam'
        ? quotes.filter(q => typeof q.recaptcha_score === 'number' && q.recaptcha_score < 0.5)
        : filterStatus === 'borderline'
          ? quotes.filter(q => typeof q.recaptcha_score === 'number' && q.recaptcha_score >= 0.5 && q.recaptcha_score < 0.7)
          : quotes.filter(q => q.status === filterStatus)
  );

  const spamCount = quotes.filter(q => typeof q.recaptcha_score === 'number' && q.recaptcha_score < 0.5).length;
  const borderlineCount = quotes.filter(q => typeof q.recaptcha_score === 'number' && q.recaptcha_score >= 0.5 && q.recaptcha_score < 0.7).length;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/5 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Quote Requests</h1>
              <p className="text-muted-foreground">Manage booking inquiries and quote requests</p>
            </div>
          </div>
          <button
            onClick={fetchQuotes}
            className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {['all', 'new', 'contacted', 'quoted', 'booked'].map((status) => {
            const count = status === 'all' ? quotes.length : quotes.filter(q => q.status === status).length;
            const StatusIcon = status === 'all' ? MessageSquare : statusIcons[status];
            return (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`p-4 rounded-xl border transition-all ${
                  filterStatus === status 
                    ? 'bg-primary/20 border-primary' 
                    : 'bg-card border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <StatusIcon className="w-4 h-4" />
                  <span className="text-sm font-medium capitalize">{status}</span>
                </div>
                <p className="text-2xl font-bold">{count}</p>
              </button>
            );
          })}
        </div>

        {/* Spam-score quick filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setFilterStatus('borderline')}
            className={`px-3 py-2 rounded-full text-xs font-semibold border flex items-center gap-1.5 transition-all ${
              filterStatus === 'borderline'
                ? 'bg-yellow-500/25 border-yellow-500/60 text-yellow-300'
                : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20'
            }`}
            data-testid="filter-borderline"
          >
            <Shield className="w-3 h-3" />
            Borderline (0.50–0.69)
            <span className="ml-1 opacity-80">{borderlineCount}</span>
          </button>
          <button
            onClick={() => setFilterStatus('spam')}
            className={`px-3 py-2 rounded-full text-xs font-semibold border flex items-center gap-1.5 transition-all ${
              filterStatus === 'spam'
                ? 'bg-red-500/25 border-red-500/60 text-red-300'
                : 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
            }`}
            data-testid="filter-spam"
          >
            <ShieldAlert className="w-3 h-3" />
            Likely bot (&lt; 0.50)
            <span className="ml-1 opacity-80">{spamCount}</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quote List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-lg font-bold mb-4">
              {filterStatus === 'all' ? 'All Requests' : `${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Requests`}
              <span className="text-muted-foreground ml-2">({filteredQuotes.length})</span>
            </h2>
            
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No quote requests found
              </div>
            ) : (
              filteredQuotes.map((quote) => {
                const StatusIcon = statusIcons[quote.status] || AlertCircle;
                return (
                  <motion.div
                    key={quote.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setSelectedQuote(quote)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedQuote?.id === quote.id
                        ? 'bg-primary/10 border-primary'
                        : 'bg-card border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold">{quote.firstName} {quote.lastName}</h3>
                        <p className="text-sm text-muted-foreground">{quote.eventName}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${statusColors[quote.status]}`}>
                        <StatusIcon className="w-3 h-3" />
                        {quote.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {quote.eventDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {quote.eventLocation?.split(',')[0]}
                      </span>
                      <RecaptchaBadge score={quote.recaptcha_score} />
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Quote Detail */}
          <div className="lg:col-span-2">
            {selectedQuote ? (
              <motion.div
                key={selectedQuote.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card border border-border rounded-2xl p-6"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6 pb-6 border-b border-border">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedQuote.firstName} {selectedQuote.lastName}</h2>
                    <p className="text-muted-foreground">{selectedQuote.company || 'Individual'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedQuote.status}
                      onChange={(e) => updateQuoteStatus(selectedQuote.id, e.target.value)}
                      className={`px-3 py-2 rounded-lg border font-medium ${statusColors[selectedQuote.status]}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="quoted">Quoted</option>
                      <option value="booked">Booked</option>
                      <option value="declined">Declined</option>
                    </select>
                    <button
                      onClick={() => deleteQuote(selectedQuote.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Contact Information
                    </h3>
                    <div className="space-y-3">
                      <a href={`mailto:${selectedQuote.email}`} className="flex items-center gap-3 text-primary hover:underline">
                        <Mail className="w-4 h-4" />
                        {selectedQuote.email}
                      </a>
                      <a href={`tel:${selectedQuote.phone}`} className="flex items-center gap-3 text-primary hover:underline">
                        <Phone className="w-4 h-4" />
                        {selectedQuote.phone}
                      </a>
                      {selectedQuote.company && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Building className="w-4 h-4" />
                          {selectedQuote.company}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Event Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Event:</span>
                        <span className="font-medium">{selectedQuote.eventName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">{selectedQuote.eventDate}</span>
                      </div>
                      {selectedQuote.eventTime && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Time:</span>
                          <span className="font-medium">{selectedQuote.eventTime}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location:</span>
                        <span className="font-medium">{selectedQuote.eventLocation}</span>
                      </div>
                      {selectedQuote.venueType && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Venue Type:</span>
                          <span className="font-medium">{selectedQuote.venueType}</span>
                        </div>
                      )}
                      {selectedQuote.expectedGuests && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Expected Guests:</span>
                          <span className="font-medium">{selectedQuote.expectedGuests}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services Requested */}
                {selectedQuote.serviceType?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-lg flex items-center gap-2 mb-3">
                      <Music className="w-5 h-5 text-primary" />
                      Services Requested
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedQuote.serviceType.map((service, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-primary/20 text-primary rounded-full text-sm font-medium">
                          {service}
                        </span>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                      {selectedQuote.performanceDuration && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="font-medium">{selectedQuote.performanceDuration}</span>
                        </div>
                      )}
                      {selectedQuote.numberOfPerformers && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Performers:</span>
                          <span className="font-medium">{selectedQuote.numberOfPerformers}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Budget & Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {selectedQuote.budget && (
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-primary" />
                        Budget Range
                      </h3>
                      <span className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg font-medium inline-block">
                        {selectedQuote.budget}
                      </span>
                    </div>
                  )}
                  {selectedQuote.howDidYouHear && (
                    <div>
                      <h3 className="font-bold text-lg mb-2">How They Found Us</h3>
                      <span className="text-muted-foreground">{selectedQuote.howDidYouHear}</span>
                    </div>
                  )}
                </div>

                {/* Special Requests */}
                {selectedQuote.specialRequests && (
                  <div className="mb-6">
                    <h3 className="font-bold text-lg flex items-center gap-2 mb-3">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      Special Requests / Notes
                    </h3>
                    <div className="p-4 bg-background rounded-xl border border-border">
                      <p className="text-foreground whitespace-pre-wrap">{selectedQuote.specialRequests}</p>
                    </div>
                  </div>
                )}

                {/* Submitted Date + Spam Score */}
                <div className="pt-4 border-t border-border text-sm text-muted-foreground flex flex-wrap items-center justify-between gap-3">
                  <span>Submitted: {formatDate(selectedQuote.created_at || selectedQuote.submitted_at)}</span>
                  <RecaptchaBadge score={selectedQuote.recaptcha_score} />
                </div>
              </motion.div>
            ) : (
              <div className="bg-card border border-border rounded-2xl p-12 text-center">
                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Quote Request</h3>
                <p className="text-muted-foreground">Click on a quote request from the list to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuoteRequests;
